import React, { useState, useRef, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Dropdown, Input, AutoComplete, Checkbox, Flex, Typography } from 'antd';
import {
  PlusOutlined,
  FileAddOutlined,
  SearchOutlined,
  UploadOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useLazyFetchSearchResultsQuery } from '../../../features/search/searchApi';
import { useLazyFetchAllTemplatesQuery } from '../../../features/template/templatesApi';
import './MainChatAiWindow.css';
const ShowMoreFileDrawer = lazy(() => import('./ShowMoreFileDrawer'));

const { Text } = Typography;

const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 30;

const AttachFileDropdown = ({
  setTemplateData,
  onAttach,
  setTemplateTitle,
  setTemplateId,
  transformData,
  selectedFiles = [],
  onRemoveFile = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [options, setOptions] = useState([]);
  const [hoveredTemplateMenu, setHoveredTemplateMenu] = useState(false);
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [showMoreFileDrawer, setShowMoreFileDrawer] = useState(false);
  const [showMoreFileItemsType, setShowMoreFileItemsType] = useState(null);

  const [triggerTemplates, templatesData] = useLazyFetchAllTemplatesQuery();
  const timeoutRef = useRef(null);
  const prevRef = useRef({ searchTerm });
  const inputRef = useRef(null);

  const onChangeAutoCompleteValue = (searchData) => {
    setAutoCompleteValue(searchData);
    const match = searchData?.match(/^(.+?)\s*\[id:\s*([a-f0-9\-]+)\]$/i);
    const title = match?.[1];
    const id = match?.[2];
    if (id) {
      handleFileAttach({ id, title }, 'template');
    }
  };
  const [triggerSearch, { data, isFetching, isLoading, isError, error, reset }] = useLazyFetchSearchResultsQuery();

  useEffect(() => {
    triggerTemplates({ query: '', category: '' });
  }, []);

  useEffect(() => {
    if (transformData?.data?.response?.data?.data) {
      setTemplateData(transformData?.data?.response?.data?.data);
      setTemplateTitle(transformData?.data?.response?.data?.title);
    }
  }, [transformData]);

  useEffect(() => {
    if (templatesData?.data?.templates?.data?.data) {
      const data = templatesData?.data?.templates?.data?.data?.map((item) => {
        return item?.templates;
      });
      const desiredResult = data?.flatMap((item) => {
        return item;
      });
      setOptions(
        desiredResult?.map((item) => {
          return {
            value: `${item?.title}[id: ${item?.id}]`,
          };
        })
      );
    }
  }, [templatesData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (prevRef.current.searchTerm !== searchTerm.trim()) {
        if (searchTerm.trim()) {
          triggerSearch({
            query: searchTerm.trim(),
            size: PAGE_SIZE,
            from: 0,
            searchType: hoveredMenu === 'worksapce' ? 'workspaces' : 'documents',
            filter: '',
          });
        }

        // Update the reference
        prevRef.current = {
          searchTerm: searchTerm.trim(),
        };
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [searchTerm, triggerSearch]);

  // handle file click
  const handleFileAttach = useCallback(
    (doc, type) => {
      setSearchTerm('');
      setHoveredMenu(null);
      setDropdownVisible(false);
      onAttach(doc, type);
    },
    [onAttach, setSearchTerm, setHoveredMenu, setDropdownVisible]
  );

  const onFileSelect = (item, type) => {
    onAttach(item, type);
  };

  // Helper function to remove selected files
  const handleRemoveFile = (item, type) => {
    if (onRemoveFile) {
      onRemoveFile(item, type);
    }
  };

  const handleShowMore = (items, type) => {
    setShowMoreFileDrawer(true);
    setShowMoreFileItemsType(type);
    setDropdownVisible(false);
  };

  const selectedKeySet = useMemo(() => {
    return new Set(selectedFiles.map((file) => `${file.id}:${file.type}`));
  }, [selectedFiles]);

  const isItemSelected = useCallback(
    (item, type) => {
      return selectedKeySet.has(`${item.id}:${type}`);
    },
    [selectedKeySet]
  );

  // Helper function to handle checkbox toggle
  const handleCheckboxToggle = (item, type) => {
    if (isItemSelected(item, type)) {
      // If item is selected, remove it
      handleRemoveFile(item, type);
    } else {
      // If item is not selected, add it
      onFileSelect(item, type);
    }
  };

  const renderDocumentMenu = () => {
    // Determine which data to use based on hoveredMenu
    const items = hoveredMenu === 'worksapce' ? data?.workspaces : data?.docs;
    const totalCount = hoveredMenu === 'worksapce' ? data?.totalWorkspaces : data?.totalDocuments;
    const placeholder = hoveredMenu === 'worksapce' ? 'Search workspace' : 'Search document';

    return (
      <div className="doc-submenu">
        <Input
          placeholder={placeholder}
          suffix={<SearchOutlined />}
          size="middle"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="doc-search-input"
          onFocus={() => {
            inputRef.current = true;
          }}
          onBlur={() => {
            inputRef.current = false;
          }}
        />
        <div className="doc-list">
          {isFetching || isLoading ? (
            <div className="doc-item">Loading...</div>
          ) : isError ? (
            <div className="doc-item error">Error: {error?.message || 'Something went wrong'}</div>
          ) : items?.length > 0 ? (
            <>
              {items.slice(0, 3).map((item) => (
                <Flex key={item.id} align="center" justify="space-between">
                  <div
                    className="doc-item"
                    onClick={() => handleFileAttach(item, hoveredMenu === 'worksapce' ? 'workspace' : 'file')}
                    style={{ alignItems: 'center', cursor: 'pointer' }}
                  >
                    <FileTextOutlined style={{ fontSize: 14, marginRight: 8, color: '#555' }} />
                    <Flex vertical gap={4} style={{ width: '140px' }}>
                      <Text ellipsis={{ tooltip: item.title || item.name }}>{item.title || item.name}</Text>
                      <Text
                        type="secondary"
                        ellipsis={{ tooltip: item?.workspace_info?.title }}
                        style={{ fontSize: 12 }}
                      >
                        {item?.workspace_info?.title}
                      </Text>
                    </Flex>
                  </div>
                  <div className="doc-item-actions">
                    <Checkbox
                      checked={isItemSelected(item, hoveredMenu === 'worksapce' ? 'workspace' : 'file')}
                      onChange={() => handleCheckboxToggle(item, hoveredMenu === 'worksapce' ? 'workspace' : 'file')}
                    />
                  </div>
                </Flex>
              ))}
              {totalCount > 3 && (
                <div
                  className="doc-item show-more"
                  onClick={() => handleShowMore(items, hoveredMenu === 'worksapce' ? 'workspace' : 'file')}
                >
                  ...{totalCount - 3} more results
                </div>
              )}
            </>
          ) : (
            <div className="doc-item">{searchTerm ? 'No results found' : 'Start typing to search...'}</div>
          )}
        </div>
      </div>
    );
  };

  const handleMouseEnter = (menuKey) => {
    clearTimeout(timeoutRef.current);
    if (hoveredMenu !== menuKey) {
      setSearchTerm('');
      reset();
    }
    setHoveredMenu(menuKey);
  };

  const handleMouseLeave = () => {
    if (!inputRef.current) {
      timeoutRef.current = setTimeout(() => {
        setHoveredMenu(null);
        setSearchTerm('');
        reset();
      }, 200);
    }
  };

  const handleTemplateMouseLeave = () => {
    setTimeout(() => {
      setHoveredTemplateMenu(false);
    }, 200);
  };

  const mainMenu = (
    <div className="custom-dropdown">
      <div className="dropdown-item" onMouseEnter={() => handleMouseEnter('document')} onMouseLeave={handleMouseLeave}>
        <FileAddOutlined />
        <span>Add OneDoc document</span>
        <RightOutlined className="submenu-arrow" />
        {hoveredMenu === 'document' && <div className="submenu-container">{renderDocumentMenu()}</div>}
      </div>

      <div className="dropdown-item" onMouseEnter={() => handleMouseEnter('worksapce')} onMouseLeave={handleMouseLeave}>
        <AppstoreOutlined />
        <span>Add OneDoc Workspace</span>
        <RightOutlined className="submenu-arrow" />
        {hoveredMenu === 'worksapce' && <div className="submenu-container">{renderDocumentMenu()}</div>}
      </div>

      <div
        className="dropdown-item"
        onMouseEnter={() => {
          setHoveredTemplateMenu(true);
        }}
        onMouseLeave={handleTemplateMouseLeave}
      >
        <AppstoreOutlined />
        <span>Add OneDoc Template</span>
        <RightOutlined className="submenu-arrow" />
        {hoveredTemplateMenu && (
          <div className="submenu-container">
            <AutoComplete
              className="flex alignCenter"
              placeholder="Search Templates"
              size="middle"
              allowClear
              value={autoCompleteValue}
              options={options}
              onChange={onChangeAutoCompleteValue}
              style={{ marginTop: '16px', width: '100%' }}
              suffixIcon={<SearchOutlined />}
            />
          </div>
        )}
      </div>

      <div className="dropdown-item disabled">
        <UploadOutlined />
        <span>Upload from computer</span>
      </div>
    </div>
  );

  return (
    <>
      {showMoreFileDrawer && (
        <Suspense fallback={<div>Loading...</div>}>
          <ShowMoreFileDrawer
            setShowMoreFileDrawer={setShowMoreFileDrawer}
            showMoreFileDrawer={showMoreFileDrawer}
            showMoreFileItems={showMoreFileItemsType !== 'workspace' ? data?.docs : data?.workspaces}
            onFileSelect={onFileSelect}
            showMoreFileItemsType={showMoreFileItemsType}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onRemoveFile={handleRemoveFile}
            isItemSelected={(item, type) => isItemSelected(item, type)}
          />
        </Suspense>
      )}
      <Dropdown
        overlay={mainMenu}
        trigger={['click']}
        placement="topLeft"
        arrow
        open={dropdownVisible}
        onOpenChange={setDropdownVisible}
      >
        <div className="plus-icon-button">
          <PlusOutlined />
        </div>
      </Dropdown>
    </>
  );
};

export default AttachFileDropdown;
